/*
 * Copyright (c) 2026.
 * Spin in het Web B.V. Apeldoorn.
 * Deze software mag alleen gebruikt / bekeken / gewijzigd worden volgens de voorwaarden die zijn vastgelegd in een bijbehorende schriftelijke overeenkomst met Spin in het Web B.V.
 */

/**
 * Auteur: Jelmer Jellema - Spin in het Web B.V.
 * Gegenereerd met Junie (AI), 12-05-2026
 *
 * Component voor de rechter toolbar-zone (#menurechts) van het canvas.
 * Vervangt de AngularJS #menurechts div (tot golf 4f als Angular 21 Custom Element).
 */
import { Component, Input, ChangeDetectionStrategy, HostListener, computed, inject, signal } from '@angular/core';
import { TranslateModule, TranslatePipe } from '@ngx-translate/core';
import { BaseIslandComponent } from '../base-island.component';
import { BeginneruitlegDirective } from '../../directives/beginnersuitleg/beginnersuitleg.directive';
import { WeergaveInstellingenService } from '../../services/weergave-instellingen.service';
import { VideoStatusService } from '../../services/video-status.service';
import { SimulatieStatusService } from '../../services/simulatie-status.service';
import { SessieService } from '../../services/sessie.service';
import type { SwitchProject } from '../../app/backend/userdata.types';
import { DlModel } from '../../app/model/dl-model';
import { ModelbeheerService } from '../../app/model/modelbeheer.service';
import { GlobalModelcanvasService } from '../../app/model/global-modelcanvas.service';
import { SamenwerkprotocolService } from '../../app/samenwerking/samenwerkprotocol.service';

/** Projectenlijst gegroepeerd per domein, afgeleid uit de lijst in de userdata. */
export type SwitchProjectenMap = Record<string, SwitchProject[]>;

@Component({
    selector: 'dl-sim-toolbar',
    standalone: true,
    templateUrl: './dl-sim-toolbar.component.html',
    styleUrls: ['./dl-sim-toolbar.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [TranslateModule, TranslatePipe, BeginneruitlegDirective],
})
export class DlSimToolbarComponent extends BaseIslandComponent {

    /**
     * De weergave-instellingen: als de beginnersmodus aan is, worden de title-attributen op de
     * knoppen onderdrukt en toont de beginnersuitleg-directive eigen uitlegballonnen.
     */
    protected readonly weergaveInstellingen = inject(WeergaveInstellingenService);

    /**
     * De videostatus: een eventuele instructievideo wijkt voor de projectswitch.
     */
    private readonly videoStatus = inject(VideoStatusService);

    /**
     * De sim-sessiestate (golf 3d): of er gesimuleerd wordt, de selectievlaggen van de
     * toestandsknoppen, welke histories aanstaan en de eerstvolgende simulatiestap. Het
     * simcanvas schrijft ze; wij lezen ze rechtstreeks.
     */
    protected readonly simulatieStatus = inject(SimulatieStatusService);

    /**
     * De sessie (golf a1): het huidige project en de projecten waarheen overgestapt kan worden,
     * plus of dit project het snelste-padgedrag laat overschrijven.
     */
    protected readonly sessie = inject(SessieService);

    /**
     * Het modelbeheer (golf 4c): de projectwissel en het uitloggen gaan rechtstreeks naar de
     * service.
     */
    protected readonly modelbeheer = inject(ModelbeheerService);

    /**
     * Het modelcanvas: of er iets op staat (golf 4d), en het afronden
     * van een half ingevoerde naam vóór een simulatie (golf 4e).
     */
    private readonly globalModelcanvas = inject(GlobalModelcanvasService);

    /**
     * De samenwerk-baas: sinds golf 4e vraagt de toolbar zijn simulatieacties daar zelf aan.
     */
    private readonly samenwerkprotocol = inject(SamenwerkprotocolService);

    /**
     * Of het model leeg is: dan staan de simulatieknoppen uit. Tijdens het laden van een model is het
     * lege canvas geen leeg model.
     */
    protected readonly modelLeeg = computed(() => !this.modelbeheer.laden() && this.globalModelcanvas.leeg());

    constructor() {
        super();
    }

    /**
     * Het modeldocument: of er in dit model gewerkt mag worden. Sinds golf a3 eigendom van `DlModel`.
     */
    protected readonly dlModel = inject(DlModel);

    // --- Inputs ---

    /**
     * Het effectieve modelleerniveau; de nextStep-knop is alleen zichtbaar bij >= 40.
     */
    @Input()
    set modelleerniveau(waarde: number) {
        this._modelleerniveau = waarde;
        this.cdr.markForCheck();
    }
    protected _modelleerniveau = 0;

    /**
     * Actief-state van de fastest-path knop.
     */
    @Input()
    set fastestPathAan(waarde: boolean) {
        this._fastestPathAan = waarde;
        this.cdr.markForCheck();
    }
    protected _fastestPathAan = false;

    // --- De knoppen ---

    /**
     * Wat elke knop bij de samenwerk-baas aanvraagt; sinds golf 4e roept de toolbar die zelf aan. De
     * baas beslist of wij het uitvoeren of dat het een verzoek aan de leider wordt.
     */
    protected doeSim(actie: string): void {
        this.samenwerkprotocol.doeSim(actie);
    }

    /**
     * De twee simulatieknoppen ronden eerst het bewerken op het modelcanvas af, zoals een klik in het
     * lege canvas: anders blijft een half ingevoerde naam open staan, en legt de eerstvolgende klik hem
     * vast als wijziging die de simulatie weer sluit (golf 3.6, test 19).
     *
     * Een nog niet opgeslagen model wordt eerst opgeslagen (met een melding vooraf); lukt dat niet of
     * annuleert de gebruiker, dan start er geen simulatie.
     */
    protected async simuleer(actie: 'fullOrStop' | 'nextstep'): Promise<void> {
        await this.globalModelcanvas.rondBewerkingAf();
        if (!await this.modelbeheer.slaOpVoorSimulatie()) {
            return;
        }
        this.samenwerkprotocol.doeSim(actie);
    }

    // --- Interne state ---

    /** Open/dicht state van de project-switch dropdown. */
    protected readonly switchProjectOpen = signal(false);

    /**
     * De projectenlijst zonder het huidige project, gegroepeerd per domein.
     */
    protected readonly gegroepeerdeProjecten = computed<SwitchProjectenMap>(() => {
        const huidigId = this.sessie.projectId();
        const groepen: SwitchProjectenMap = {};
        for (const project of this.sessie.switchProjecten()) {
            if (project.project_id !== huidigId) {
                (groepen[project.domein_naam] = groepen[project.domein_naam] || []).push(project);
            }
        }
        return groepen;
    });

    /** De domeinnamen (keys) van de gegroepeerde projectenlijst. */
    protected readonly domeinen = computed(() => Object.keys(this.gegroepeerdeProjecten()));

    /** Of de simulatie draait: bepaalt de disabled-state van selectie, history en fastest-path. */
    protected readonly simulatieAan = this.simulatieStatus.simuleren;

    /** De vier selectievlaggen van de toestandsknoppen. */
    protected readonly stateSelectie = this.simulatieStatus.stateSelectie;

    /** Welke histories aanstaan. */
    protected readonly history = this.simulatieStatus.history;

    /** De volgende simulatiestap; bepaalt CSS-klasse en disabled-state van de nextStep-knop. */
    protected readonly volgendeSimStap = this.simulatieStatus.volgendeStap;

    /** Toont de domeinnaam in de dropdown zodra er meer dan één domein is. */
    protected readonly metDomein = computed(() => this.domeinen().length > 1);

    // --- Hulpmethoden ---

    /**
     * Toggelt de project-switch dropdown. De klik wordt binnen de dropdown-container gestopt
     * (zie template), zodat de document-click-listener hem niet meteen weer sluit. Een eventuele
     * instructievideo gaat daarbij uit.
     */
    protected toggleSwitchProject(): void {
        this.videoStatus.cancel();
        // De uitlegballon van deze knop hangt in de ballon-laag en zou over de dropdown vallen
        BeginneruitlegDirective.sluitAlle();
        this.switchProjectOpen.update(open => !open);
    }

    /**
     * Sluit de dropdown bij een klik buiten de dropdown-container (klikken binnenin stoppen de propagatie).
     */
    @HostListener('document:click')
    protected sluitSwitchProject(): void {
        this.switchProjectOpen.set(false);
    }
}
